import React, { useState, useRef, useEffect } from 'react';
import { Card, Input, Button, message, Tag, Space, Spin, Popover, Typography, Collapse, Tooltip, Row, Col, Modal } from 'antd';
import {
    SearchOutlined,
    BarChartOutlined,
    ReloadOutlined,
    WarningOutlined,
    ThunderboltOutlined,
    CopyOutlined,
    CheckOutlined,
    DownloadOutlined,
    HistoryOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import html2canvas from 'html2canvas';
import AuthService from '../../services/authService';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import { useQuotaStatus } from '../../hooks/useQuotaStatus';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import './ProductGenerate.css';

const { Text } = Typography;

const fmt = n => (n >= 10000 ? `${Math.round(n / 1000) / 10}만` : n?.toLocaleString() ?? '-');

const CopyBtn = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };
    return (
        <span data-no-capture="true">
        <Button
            size="small"
            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            onClick={handleCopy}
            style={{ fontSize: 11, color: copied ? '#22c55e' : '#6366f1', borderColor: copied ? '#86efac' : '#c7d2fe' }}
        >
            {copied ? '복사됨' : '복사'}
        </Button>
        </span>
    );
};

const ProductGenerate = () => {
    const { quota } = useQuotaStatus('productGenerate');
    const [keyword, setKeyword] = useState('');
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [showAllTop10, setShowAllTop10] = useState(false);
    const [top10Collapsed, setTop10Collapsed] = useState(false);

    const [tagsState, setTagsState] = useState({ loading: false, data: null, error: false });
    const [tagsTotal, setTagsTotal] = useState(0);
    const tagsJobRef = useRef(0);
    const activeTagsJobIdRef = useRef(null);

    const [aiLoading, setAiLoading] = useState(false);
    const [aiResults, setAiResults] = useState([]);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyOffset, setHistoryOffset] = useState(0);
    const [historyHasMore, setHistoryHasMore] = useState(false);
    const cardRefs = useRef([]);
    const [savingIdx, setSavingIdx] = useState(null);

    const handleSaveImage = async (idx) => {
        const el = cardRefs.current[idx];
        if (!el) return;
        setSavingIdx(idx);
        try {
            const canvas = await html2canvas(el, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                ignoreElements: element => element.dataset?.noCapture === 'true',
            });
            const link = document.createElement('a');
            link.download = `분석결과_${idx === 0 ? '최신' : idx + 1}번째.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch {
            message.error('이미지 저장에 실패했습니다.');
        } finally {
            setSavingIdx(null);
        }
    };
    const includeVolume = true;
    const autoAnalyzeRef = useRef(false);
    const [categoryMismatch, setCategoryMismatch] = useState(null); // { myCategory, topCategory }

    const fetchTop10Tags = async competitors => {
        if (activeTagsJobIdRef.current) {
            AuthService.authAxios
                .post('/product/analysis/top10-tags/cancel', { job_id: activeTagsJobIdRef.current })
                .catch(() => {});
            activeTagsJobIdRef.current = null;
        }
        const myJob = ++tagsJobRef.current;
        const smartUrls = (competitors || [])
            .map(c => c.mallUrl)
            .filter(u => typeof u === 'string' && u.includes('smartstore.naver.com'));
        if (smartUrls.length === 0) return;

        setTagsTotal(smartUrls.length);
        setTagsState({ loading: true, data: {}, error: false });

        try {
            const submitRes = await AuthService.authAxios.post('/product/analysis/top10-tags', { urls: smartUrls });
            if (tagsJobRef.current !== myJob) return;
            if (!submitRes.data.success) { setTagsState({ loading: false, data: null, error: true }); return; }

            const { job_id } = submitRes.data;
            activeTagsJobIdRef.current = job_id;
            let offset = 0, completed = false;
            const startTime = Date.now();

            while (!completed) {
                if (tagsJobRef.current !== myJob) return;
                if (Date.now() - startTime > 120000) { setTagsState(p => ({ ...p, loading: false, error: true })); break; }
                await new Promise(r => setTimeout(r, 500));
                if (tagsJobRef.current !== myJob) return;
                try {
                    const streamRes = await AuthService.authAxios.get('/product/analysis/top10-tags/stream', { params: { job_id, offset } });
                    const { status, items } = streamRes.data;
                    if (items && items.length > 0) {
                        offset += items.length;
                        if (tagsJobRef.current !== myJob) return;
                        setTagsState(prev => {
                            const newData = { ...(prev.data || {}) };
                            items.forEach(item => {
                                if (item.success) newData[item.url] = { tags: item.tags || [], todayVisit: item.todayVisit ?? null, totalVisit: item.totalVisit ?? null };
                            });
                            return { ...prev, data: newData };
                        });
                    }
                    if (status === 'completed' || status === 'cancelled') {
                        completed = true;
                        if (tagsJobRef.current !== myJob) return;
                        setTagsState(prev => ({ ...prev, loading: false }));
                    }
                } catch { /* 일시적 오류 무시 */ }
            }
        } catch {
            setTagsState({ loading: false, data: null, error: true });
        }
    };

    const handleSearch = async () => {
        if (!keyword.trim()) { message.warning('키워드를 입력해주세요.'); return; }
        if (!url.trim()) { message.warning('상품 URL을 입력해주세요.'); return; }
        if (!quota.unlimited && !quota.loading && quota.remaining <= 0) {
            message.error('오늘 상품명키워드생성 한도를 모두 사용하였습니다.'); return;
        }
        setLoading(true);
        setResult(null);
        setAiResults([]);
        setTagsState({ loading: false, data: null, error: false });
        setTagsTotal(0);
        setShowAllTop10(false);
        setCategoryMismatch(null);
        try {
            const res = await AuthService.authAxios.post('/product-generate/top10', {
                keyword: keyword.trim(),
                url: url.trim() || undefined,
            });
            if (res.data.success) {
                setResult(res.data);
                dispatchQuotaUpdated('productGenerate');
                AuthService.authAxios
                    .post('/log/history-update', {
                        type: 'productGenerate',
                        inputLog: `keyword=${keyword.trim()}|url=${url.trim()}`,
                    })
                    .catch(() => {});

                // 카테고리 불일치 감지
                const myProd = res.data.myProduct;
                const top10List = res.data.top10 || [];
                let mismatch = null;
                if (myProd?.category) {
                    const catCounts = {};
                    top10List.forEach(c => { if (c.category) catCounts[c.category] = (catCounts[c.category] || 0) + 1; });
                    const topCat = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
                    const myLv2 = myProd.category.split(' > ')[1] || myProd.category;
                    const topLv2 = topCat.split(' > ')[1] || topCat;
                    if (topCat && myLv2 && topLv2 && myLv2 !== topLv2) {
                        mismatch = { myCategory: myProd.category, topCategory: topCat };
                    }
                }
                setCategoryMismatch(mismatch);

                if (!mismatch) {
                    autoAnalyzeRef.current = true;
                    fetchTop10Tags(res.data.top10);
                }
            } else {
                message.error(res.data.message || '조회에 실패했습니다.');
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.detail || '서버와 통신 중 오류가 발생했습니다.';
            if (err.response?.status === 429) { message.error(msg); dispatchQuotaUpdated('productGenerate'); }
            else message.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async (offset = 0, append = false) => {
        setHistoryLoading(true);
        try {
            const res = await AuthService.authAxios.get('/log/history', {
                params: { gubun: 'productGenerate', offset },
            });
            if (res.data.success) {
                const items = res.data.data || [];
                setHistoryData(prev => (append ? [...prev, ...items] : items));
                setHistoryHasMore(res.data.hasMore ?? items.length === 10);
                setHistoryOffset(offset + items.length);
            }
        } catch {
            message.error('조회 기록을 불러오지 못했습니다.');
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleApplyHistory = inputStr => {
        const parts = Object.fromEntries(
            inputStr.split('|').map(p => {
                const idx = p.indexOf('=');
                return [p.slice(0, idx), p.slice(idx + 1)];
            })
        );
        if (parts.keyword) setKeyword(parts.keyword);
        if (parts.url) setUrl(parts.url);
        setShowHistoryModal(false);
    };

    const handleDeleteHistory = async id => {
        try {
            await AuthService.authAxios.post('/log/delete', { id });
            setHistoryData(prev => prev.filter(h => h.id !== id));
        } catch {
            message.error('삭제에 실패했습니다.');
        }
    };

    const handleAiAnalyze = async () => {
        if (!result) return;
        setAiLoading(true);
        try {
            const res = await AuthService.authAxios.post('/product-generate/ai-analyze', {
                keyword: result.keyword,
                top10: result.top10,
                tagsData: tagsState.data || {},
                myProduct: result.myProduct || null,
                existingTagVolumes: includeVolume && aiResults.length > 0 ? (aiResults[0].tagVolumes || {}) : null,
                includeVolume,
            });
            if (res.data.success) {
                setAiResults(prev => [res.data, ...prev]);
            } else {
                message.error(res.data.message || 'AI 분석에 실패했습니다.');
            }
        } catch (err) {
            message.error(err.response?.data?.message || 'AI 분석 중 오류가 발생했습니다.');
        } finally {
            setAiLoading(false);
        }
    };

    const getTagsForCompetitor = c => tagsState?.data?.[c.mallUrl]?.tags || c.tags || [];

    const myProduct = result?.myProduct;
    const myTags = new Set((myProduct?.tags || '').split(',').map(t => t.trim()).filter(Boolean));
    const tagsLoading = tagsState?.loading;
    const tagsError = tagsState?.error;
    const top10 = result?.top10 || [];
    const tagVolumes = aiResults[0]?.tagVolumes || {};
    const uniqueCategories = [...new Set(top10.map(c => c.category).filter(Boolean))];

    const tagsFullyLoaded = !tagsLoading && tagsState.data !== null;

    const top10TagFreq = (() => {
        const freq = {};
        top10.forEach(c => {
            getTagsForCompetitor(c).forEach(tag => {
                freq[tag] = (freq[tag] || 0) + 1;
            });
        });
        return Object.entries(freq).sort((a, b) => b[1] - a[1] || (tagVolumes[b[0]] || 0) - (tagVolumes[a[0]] || 0));
    })();

    useEffect(() => {
        if (autoAnalyzeRef.current && tagsFullyLoaded && result && !aiLoading) {
            autoAnalyzeRef.current = false;
            handleAiAnalyze();
        }
    }, [tagsFullyLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    const missingFields = [];
    if (top10.some(c => c.wishCount == null)) missingFields.push('찜수');
    if (top10.some(c => c.reviewCount == null)) missingFields.push('리뷰수');
    if (top10.some(c => c.price == null)) missingFields.push('가격');

    const tagsCollected = Object.keys(tagsState.data || {}).length;
    const progressStep = tagsLoading ? 1 : aiLoading ? 2 : aiResults.length > 0 ? 3 : result ? 1 : 0;

    const ProgressSteps = ({ activeStep }) => {
        const steps = [
            { label: '키워드 검색', sub: '상품 데이터 수집' },
            { label: '태그 수집', sub: tagsTotal > 0 ? `${tagsCollected}/${tagsTotal}개 완료` : '대기 중' },
            { label: '최적화 분석', sub: '상품명·태그 생성' },
        ];
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: 520, margin: '0 auto', padding: '0 16px' }}>
                {steps.map((s, i) => {
                    const done = i < activeStep;
                    const active = i === activeStep;
                    return (
                        <React.Fragment key={i}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
                                <div style={{ width: 36, height: 36, borderRadius: '50%', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: done ? '#22c55e' : active ? '#6366f1' : '#e2e8f0', transition: 'background 0.3s' }}>
                                    {done
                                        ? <CheckOutlined style={{ color: '#fff', fontSize: 14 }} />
                                        : active
                                            ? <Spin size="small" style={{ color: '#fff' }} />
                                            : <span style={{ color: '#94a3b8', fontSize: 12, fontWeight: 700 }}>{i + 1}</span>}
                                </div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: done ? '#22c55e' : active ? '#6366f1' : '#94a3b8', textAlign: 'center', lineHeight: 1.3 }}>{s.label}</div>
                                <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', marginTop: 2 }}>{s.sub}</div>
                            </div>
                            {i < 2 && <div style={{ flex: 1, height: 2, background: done ? '#22c55e' : '#e2e8f0', marginBottom: 30, minWidth: 20, transition: 'background 0.3s' }} />}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="product-generate-page">
        <div style={{ padding: '16px 24px', maxWidth: 1500, margin: '0 auto' }}>
            {/* 검색 입력 */}
            <Card size="small" bordered={false} style={{ marginBottom: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }} bodyStyle={{ padding: '10px 16px' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} md={5}>
                        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3, fontWeight: 600 }}>
                            키워드 <span style={{ color: '#ef4444' }}>*</span>
                        </div>
                        <Input value={keyword} onChange={e => setKeyword(e.target.value)} onPressEnter={handleSearch}
                            placeholder="예: 욕실선반" disabled={loading} style={{ fontSize: 12 }} />
                    </Col>
                    <Col xs={24} md={19}>
                        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3, fontWeight: 600 }}>
                            내 상품 또는 비슷한 타사 제품 URL <span style={{ color: '#ef4444' }}>*</span>
                            <span className="pg-url-hint" style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>· 입력 시 현재 순위·태그를 반영해 더 정확한 결과를 얻을 수 있습니다</span>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Input value={url} onChange={e => setUrl(e.target.value)} onPressEnter={handleSearch}
                                placeholder="https://smartstore.naver.com/..." disabled={loading} style={{ fontSize: 12 }} allowClear />
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading} style={{ minWidth: 80, flexShrink: 0 }}>
                                조회
                            </Button>
                            <Button
                                icon={<HistoryOutlined />}
                                disabled={loading}
                                onClick={() => { setShowHistoryModal(true); fetchHistory(0, false); }}
                                style={{ flexShrink: 0 }}
                            >
                                조회기록
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* 결과 샘플 미리보기 */}
            {!result && !loading && (
                <div>
                    {/* 안내 배너 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#eff6ff', border: '1px solid #bfdbfe', padding: '14px 18px', borderRadius: 10, marginBottom: 24, color: '#1e40af', fontSize: 13, fontWeight: 500, boxShadow: '0 2px 6px rgba(37,99,235,0.05)' }}>
                        <BarChartOutlined style={{ color: '#2563eb', fontSize: 18, flexShrink: 0 }} />
                        <div>
                            키워드 입력 후 조회하면 <b>경쟁사 순위 TOP 10 분석</b>과 <b>상품명·태그·카테고리 최적화</b> 결과를 확인할 수 있습니다.
                            아래 <b>예시 화면</b>을 통해 결과 형태를 미리 확인해 보세요.
                        </div>
                    </div>

                    <Row gutter={[16, 16]}>
                        {/* LEFT: 샘플 TOP 10 */}
                        <Col xs={24} xl={10}>
                            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 16, position: 'relative', overflow: 'hidden' }}
                                bodyStyle={{ padding: '12px 16px' }}
                                title={
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <BarChartOutlined style={{ color: '#7c3aed', fontSize: 13 }} /> 키워드 순위 TOP 10
                                        <Tag style={{ fontSize: 10, fontWeight: 700, margin: 0, borderRadius: 4, border: 'none', background: '#e0f2fe', color: '#0369a1' }}>예시</Tag>
                                    </span>
                                }
                                extra={<span style={{ fontSize: 11, color: '#9ca3af' }}>1,970,829개 중</span>}
                            >
                                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: 52, fontWeight: 900, color: 'rgba(148,163,184,0.10)', pointerEvents: 'none', zIndex: 10, whiteSpace: 'nowrap', letterSpacing: 6, userSelect: 'none' }}>
                                    TOP 10
                                </div>
                                {/* MY 상품 */}
                                <div style={{ border: '2px solid #7c3aed', borderRadius: 10, padding: '14px 14px 10px', display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12, position: 'relative' }}>
                                    <div style={{ position: 'absolute', top: -11, left: 12, background: '#7c3aed', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 10 }}>MY</div>
                                    <div style={{ width: 52, height: 52, background: '#ede9fe', borderRadius: 6, flexShrink: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3, color: '#0f172a', marginBottom: 3 }}>무타공 욕실선반 화장실 수납 정리대 블랙 일자형 벽걸이 50cm</div>
                                        <div style={{ fontSize: 11, color: '#6366f1', marginBottom: 3 }}><span style={{ fontWeight: 700 }}>4위</span><span style={{ color: '#f59e0b', marginLeft: 8, fontWeight: 700 }}>18,900원</span></div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                            {['욕실선반', '무타공', '화장실수납'].map(t => <span key={t} style={{ fontSize: 10, color: '#7c3aed' }}>#{t}</span>)}
                                        </div>
                                    </div>
                                </div>
                                {/* 경쟁사 */}
                                {[
                                    { rank: 1, name: '무타공 욕실선반 화장실 수납 정리대 블랙 일자형 벽걸이', store: '인테리어마켓', price: '18,900', tags: ['욕실선반', '무타공', '화장실수납', '블랙선반', '일자선반'] },
                                    { rank: 2, name: '욕실선반 화장실 수납 스테인리스 무타공 벽부착 정리대', store: '홈데코샵', price: '22,500', tags: ['욕실수납', '스테인리스', '벽부착선반', '화장실선반'] },
                                    { rank: 3, name: '욕실 수납선반 알루미늄 방수 무타공 화장실 벽걸이', store: '욕실전문점', price: '15,800', tags: ['알루미늄선반', '방수선반', '욕실정리대'] },
                                ].map((c, idx) => (
                                    <div key={idx} style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid #f1f3f5', alignItems: 'flex-start', gap: 4 }}>
                                        <div style={{ width: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 2 }}>
                                            <span style={{ fontSize: 16, fontWeight: 900, lineHeight: 1, color: idx === 0 ? '#F59E0B' : idx === 1 ? '#94A3B8' : '#CD7F32' }}>{c.rank}</span>
                                        </div>
                                        <div style={{ width: 52, flexShrink: 0, paddingRight: 8 }}>
                                            <div style={{ width: 44, height: 44, background: '#f1f5f9', borderRadius: 5 }} />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.35, color: '#0f172a', marginBottom: 3 }}>{c.name}</div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                <span style={{ fontSize: 11, color: '#64748b', background: '#f1f5f9', padding: '0 5px', borderRadius: 3 }}>{c.store}</span>
                                                <span style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b' }}>{c.price}원</span>
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                                {c.tags.map(tag => (
                                                    <Tag key={tag} style={{ fontSize: 11, borderRadius: 8, border: 'none', padding: '0 6px', margin: 0, background: '#fee2e2', color: '#ef4444' }}>#{tag}</Tag>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div style={{ textAlign: 'center', paddingTop: 10 }}>
                                    <Button size="small" style={{ fontSize: 11, color: '#6366f1', borderColor: '#c7d2fe', background: '#f5f3ff' }}>7개 더보기 ▼</Button>
                                </div>
                            </Card>

                            {/* TOP 10 태그 빈도 */}
                            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }} bodyStyle={{ padding: '10px 14px' }}
                                title={<span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>TOP 10 사용 태그 <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>12개</span></span>}
                            >
                                <div style={{ display: 'flex', fontSize: 11, color: '#b0b8c8', fontWeight: 600, padding: '0 6px 6px', borderBottom: '1px solid #f1f3f5' }}>
                                    <span style={{ flex: 1 }}>태그</span>
                                    <span style={{ width: 72, textAlign: 'right', flexShrink: 0 }}>경쟁사 사용빈도</span>
                                    <span style={{ width: 52, textAlign: 'right', flexShrink: 0 }}>검색량</span>
                                </div>
                                {[
                                    { tag: '욕실선반', my: true, count: '9/10', vol: '4.2만' },
                                    { tag: '무타공', my: true, count: '8/10', vol: '3.8만' },
                                    { tag: '화장실수납', my: true, count: '7/10', vol: '2.9만' },
                                    { tag: '블랙선반', my: false, count: '6/10', vol: '2.1만' },
                                    { tag: '일자선반', my: false, count: '5/10', vol: '1.7만' },
                                ].map(({ tag, my, count, vol }) => (
                                    <div key={tag} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 6px', borderRadius: 5, background: my ? '#eef2ff' : 'transparent' }}>
                                        {my && <span style={{ fontSize: 9, fontWeight: 700, color: '#6366f1', background: '#c7d2fe', borderRadius: 3, padding: '1px 4px', lineHeight: 1.4, flexShrink: 0 }}>MY</span>}
                                        <span style={{ fontSize: 12, fontWeight: my ? 700 : 500, color: my ? '#4338ca' : '#475569', flex: 1 }}>#{tag}</span>
                                        <span style={{ width: 72, textAlign: 'right', fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{count}</span>
                                        <span style={{ width: 52, textAlign: 'right', fontSize: 11, color: '#b0b8c8', flexShrink: 0 }}>{vol}</span>
                                    </div>
                                ))}
                            </Card>
                        </Col>

                        {/* RIGHT: 샘플 AI 분석 결과 */}
                        <Col xs={24} xl={14}>
                            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', position: 'relative', overflow: 'hidden' }}
                                title={
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <ThunderboltOutlined style={{ color: '#f59e0b', fontSize: 13 }} /> 상품명·태그·카테고리 최적화
                                        <Tag style={{ fontSize: 10, fontWeight: 700, margin: 0, borderRadius: 4, border: 'none', background: '#e0f2fe', color: '#0369a1' }}>예시</Tag>
                                    </span>
                                }
                            >
                                <div style={{ border: '2px solid #6366f1', borderRadius: 12, padding: '16px', background: '#fafafe', position: 'relative' }}>
                                    <div style={{ position: 'absolute', top: -11, left: 14, background: '#6366f1', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 10 }}>최신</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                                        {/* 추천 상품명 */}
                                        <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', background: '#eef2ff', padding: '2px 8px', borderRadius: 4 }}>추천 상품명</span>
                                                    <span style={{ fontSize: 10, color: '#94a3b8' }}>스토어명·모델명·옵션명은 필요 시 개별적으로 넣어주세요</span>
                                                </div>
                                                <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981' }}>42 / 50자</span>
                                            </div>
                                            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', lineHeight: 1.5, marginBottom: 6 }}>무타공 욕실선반 화장실 수납 정리대 블랙 일자형 벽걸이 50cm</div>
                                            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>검색량 높은 &apos;욕실선반&apos;, &apos;무타공&apos; 키워드를 앞쪽에 배치하고, 소재·색상·형태 속성을 조합하여 가독성을 높였습니다.</div>
                                        </div>
                                        {/* 추천 태그 */}
                                        <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', background: '#eef2ff', padding: '2px 8px', borderRadius: 4 }}>추천 태그 <span style={{ fontWeight: 400, color: '#94a3b8' }}>10개</span></span>
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                                                {[
                                                    { tag: '화장실선반', vol: '4.2만', my: true }, { tag: '무타공', vol: '3.8만', my: true }, { tag: '욕실수납', vol: '2.9만', my: true },
                                                    { tag: '블랙선반', vol: '2.1만', my: false }, { tag: '일자선반', vol: '1.7만', my: false }, { tag: '수납정리대', vol: '1.5만', my: false },
                                                    { tag: '욕실정리대', vol: '1.2만', my: false }, { tag: '화장실수납', vol: '9,800', my: true }, { tag: '벽걸이선반', vol: '8,400', my: false },
                                                    { tag: '욕실일자선반', vol: '6,100', my: false },
                                                ].map(({ tag, vol, my }) => (
                                                    <div key={tag} style={{ border: `1px solid ${my ? '#a5b4fc' : '#e2e8f0'}`, borderRadius: 6, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 5, background: my ? '#eef2ff' : '#fff' }}>
                                                        {my && <span style={{ fontSize: 9, fontWeight: 700, color: '#6366f1', background: '#c7d2fe', borderRadius: 3, padding: '1px 4px', lineHeight: 1.4 }}>MY</span>}
                                                        <span style={{ fontSize: 12, fontWeight: 600, color: my ? '#4338ca' : '#1e293b' }}>#{tag}</span>
                                                        <span style={{ fontSize: 10, color: '#94a3b8' }}>{vol}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>상위 경쟁사가 공통으로 사용하여 검증된 고효율 키워드 위주로 구성하였습니다.</div>
                                        </div>
                                        {/* 카테고리 + 인사이트 */}
                                        <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', width: 58, flexShrink: 0, paddingTop: 1 }}>카테고리</span>
                                                <span style={{ fontSize: 11, color: '#334155', flex: 1, lineHeight: 1.6 }}>현재 카테고리(생활/건강 &gt; 욕실용품 &gt; 욕실장/선반)는 최적의 경로로 유지하시기 바랍니다.</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 0' }}>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', width: 58, flexShrink: 0, paddingTop: 1 }}>인사이트</span>
                                                <span style={{ fontSize: 11, color: '#334155', flex: 1, lineHeight: 1.7 }}>블랙 컬러의 모던함과 무타공 설치의 간편함을 강조하면 현재 4위에서 더 높은 순위로 진입 가능합니다.</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>
            )}

            {/* 초기 로딩 프로그레스 */}
            {loading && (
                <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 16 }}>
                    <div style={{ padding: '28px 0 20px' }}>
                        <div style={{ textAlign: 'center', marginBottom: 28 }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 12, fontSize: 13, fontWeight: 600, color: '#6366f1' }}>키워드 검색 중 · 잠시만 기다려주세요</div>
                            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>네이버 쇼핑 상위 10개 상품을 수집하고 있습니다</div>
                        </div>
                        <ProgressSteps activeStep={0} />
                    </div>
                </Card>
            )}

            {/* 결과 2컬럼 레이아웃 */}
            {result && (
                <>
                    {/* 진행 상태 바 (첫 분석 때만: 태그 수집 or 최초 AI 분석 중) */}
                    {(tagsLoading || (aiLoading && aiResults.length === 0)) && (
                        <Card bordered={false} style={{ borderRadius: 10, boxShadow: '0 1px 6px rgba(0,0,0,0.05)', marginBottom: 16, background: '#f8faff' }} bodyStyle={{ padding: '16px 24px' }}>
                            <ProgressSteps activeStep={progressStep} />
                        </Card>
                    )}

                    <Row gutter={[16, 16]}>
                        {/* LEFT: TOP 10 + 태그 */}
                        <Col xs={24} xl={10}>
                            <Card
                                bordered={false}
                                style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 16 }}
                                bodyStyle={{ padding: (top10Collapsed && !myProduct) ? 0 : '12px 16px', overflow: 'hidden' }}
                                title={
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <BarChartOutlined style={{ color: '#7c3aed', fontSize: 13 }} /> 키워드 순위 TOP 10
                                    </span>
                                }
                                extra={
                                    <Space size={6}>
                                        {!top10Collapsed && tagsLoading && <Spin size="small" />}
                                        {!top10Collapsed && tagsError && (
                                            <Button size="small" icon={<ReloadOutlined />} onClick={() => fetchTop10Tags(top10)}
                                                style={{ fontSize: 11, color: '#ef4444', borderColor: '#fca5a5' }}>재조회</Button>
                                        )}
                                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{result.totalCount?.toLocaleString() || '0'}개 중</span>
                                        <Button size="small" onClick={() => setTop10Collapsed(v => !v)}
                                            style={{ fontSize: 11, color: '#6366f1', borderColor: '#c7d2fe', background: '#f5f3ff' }}>
                                            {top10Collapsed ? '펼치기 ▼' : '접기 ▲'}
                                        </Button>
                                    </Space>
                                }
                            >
                                {/* MY 상품 - 접기 시에도 유지 */}
                                {myProduct && (
                                    <div style={{ border: '2px solid #7c3aed', borderRadius: 10, padding: '14px 14px 10px', display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: top10Collapsed ? 0 : 12, position: 'relative' }}>
                                        <div style={{ position: 'absolute', top: -11, left: 12, background: '#7c3aed', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 10 }}>MY</div>
                                        {myProduct.image
                                            ? <img src={myProduct.image} alt="" style={{ width: 52, height: 52, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                                            : <div style={{ width: 52, height: 52, background: '#ede9fe', borderRadius: 6, flexShrink: 0 }} />}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3, color: '#0f172a', marginBottom: 3 }}>{myProduct.title}</div>
                                            <div style={{ fontSize: 11, color: '#6366f1', marginBottom: 3 }}>
                                                <span style={{ fontWeight: 700 }}>{myProduct.rank > 0 ? `${myProduct.rank}위` : '1000위 밖'}</span>
                                                {myProduct.price != null && <span style={{ color: '#f59e0b', marginLeft: 8, fontWeight: 700 }}>{myProduct.price.toLocaleString()}원</span>}
                                            </div>
                                            {myProduct.category && (
                                                <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: myTags.size > 0 ? 4 : 0 }}>{myProduct.category}</div>
                                            )}
                                            {myTags.size > 0 && (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                    {[...myTags].map(tag => <span key={tag} style={{ fontSize: 10, color: '#7c3aed' }}>#{tag}</span>)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {!top10Collapsed && (<>
                                {/* 범례 */}
                                {myProduct && (
                                    <div style={{ display: 'flex', gap: 12, fontSize: 11, marginBottom: 8 }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#4f46e5', fontWeight: 600 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4f46e5', display: 'inline-block' }} /> 공동사용
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#ef4444', fontWeight: 600 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} /> 경쟁사만
                                        </span>
                                    </div>
                                )}

                                {/* 경쟁사 목록 */}
                                {(showAllTop10 ? top10 : top10.slice(0, 3)).map((c, idx) => {
                                    const tags = getTagsForCompetitor(c);
                                    return (
                                        <div key={idx} style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid #f1f3f5', alignItems: 'flex-start', gap: 4 }}>
                                            <div style={{ width: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 2 }}>
                                                <span style={{ fontSize: 16, fontWeight: 900, lineHeight: 1, color: idx === 0 ? '#F59E0B' : idx === 1 ? '#94A3B8' : idx === 2 ? '#CD7F32' : '#cbd5e1' }}>
                                                    {c.rank || idx + 1}
                                                </span>
                                            </div>
                                            <div style={{ width: 52, flexShrink: 0, paddingRight: 8 }}>
                                                {c.imageUrl ? (
                                                    <Popover content={<img src={c.imageUrl} alt="" style={{ width: 280, height: 280, objectFit: 'cover', borderRadius: 6 }} />}
                                                        placement="right" trigger="hover" styles={{ body: { padding: 4 } }}>
                                                        <img src={c.imageUrl} alt="" style={{ width: 44, height: 44, borderRadius: 5, objectFit: 'cover', cursor: 'zoom-in' }} />
                                                    </Popover>
                                                ) : (
                                                    <div style={{ width: 44, height: 44, background: '#f1f5f9', borderRadius: 5 }} />
                                                )}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.35, color: '#0f172a', marginBottom: 3 }}>{c.name}</div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                                                    {c.mallName && <span style={{ fontSize: 11, color: '#64748b', background: '#f1f5f9', padding: '0 5px', borderRadius: 3 }}>{c.mallName}</span>}
                                                    <span style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b' }}>{c.price ? `${c.price.toLocaleString()}원` : '-'}</span>
                                                    {c.mallUrl && <a href={c.mallUrl} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" style={{ fontSize: 11, color: '#6366f1' }}>링크</a>}
                                                </div>
                                                {c.category && (
                                                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 3 }}>{c.category}</div>
                                                )}
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                                    {tagsLoading && !tags.length ? (
                                                        <span style={{ fontSize: 11, color: '#94a3b8' }}>수집 중...</span>
                                                    ) : tags.map(tag => {
                                                        const vol = tagVolumes[tag];
                                                        return (
                                                            <Tag key={tag} style={{ fontSize: 11, borderRadius: 8, border: 'none', padding: '0 6px', margin: 0, background: myTags.has(tag) ? '#ede9fe' : '#fee2e2', color: myTags.has(tag) ? '#4f46e5' : '#ef4444' }}>
                                                                #{tag}{vol ? <span style={{ marginLeft: 2, color: '#94a3b8', fontSize: 10 }}>{fmt(vol)}</span> : null}
                                                            </Tag>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* 더보기 */}
                                {top10.length > 3 && (
                                    <div style={{ textAlign: 'center', paddingTop: 10 }}>
                                        <Button size="small" onClick={() => setShowAllTop10(v => !v)}
                                            style={{ fontSize: 11, color: '#6366f1', borderColor: '#c7d2fe', background: '#f5f3ff' }}>
                                            {showAllTop10 ? '접기 ▲' : `${top10.length - 3}개 더보기 ▼`}
                                        </Button>
                                    </div>
                                )}
                                </>)}
                            </Card>

                            {/* TOP 10 사용 태그 */}
                            {top10TagFreq.length > 0 && (
                                <Card
                                    bordered={false}
                                    style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                                    bodyStyle={{ padding: '10px 14px' }}
                                    title={
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                                            TOP 10 사용 태그
                                            <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>{top10TagFreq.length}개</span>
                                            {tagsLoading && <Spin size="small" />}
                                        </span>
                                    }
                                >
                                    <div style={{ display: 'flex', fontSize: 11, color: '#b0b8c8', fontWeight: 600, padding: '0 6px 6px', borderBottom: '1px solid #f1f3f5' }}>
                                        <span style={{ flex: 1 }}>태그</span>
                                        <span style={{ width: 72, textAlign: 'right', flexShrink: 0 }}>경쟁사 사용빈도</span>
                                        <span style={{ width: 52, textAlign: 'right', flexShrink: 0 }}>검색량</span>
                                    </div>
                                    <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 4 }}>
                                        {top10TagFreq.map(([tag, count]) => {
                                            const isMy = myTags.has(tag);
                                            const vol = tagVolumes[tag];
                                            return (
                                                <div key={tag} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 6px', borderRadius: 5, background: isMy ? '#eef2ff' : 'transparent' }}>
                                                    {isMy && <span style={{ fontSize: 9, fontWeight: 700, color: '#6366f1', background: '#c7d2fe', borderRadius: 3, padding: '1px 4px', lineHeight: 1.4, flexShrink: 0 }}>MY</span>}
                                                    <span style={{ fontSize: 12, fontWeight: isMy ? 700 : 500, color: isMy ? '#4338ca' : '#475569', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>#{tag}</span>
                                                    <span style={{ width: 72, textAlign: 'right', fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{count}/{top10.length}</span>
                                                    <span style={{ width: 52, textAlign: 'right', fontSize: 11, color: '#b0b8c8', flexShrink: 0 }}>{vol != null ? fmt(vol) : '-'}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card>
                            )}
                        </Col>

                        {/* RIGHT: 카테고리 경고 + 분석 결과 */}
                        <Col xs={24} xl={14}>
                            {/* 카테고리 불일치 경고 */}
                            {categoryMismatch && (
                                <div style={{ marginBottom: 12, padding: '14px 18px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <WarningOutlined style={{ color: '#f97316', fontSize: 16, flexShrink: 0 }} />
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#c2410c' }}>카테고리 불일치 — 분석 전 확인하세요</span>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#7c2d12', lineHeight: 1.7 }}>
                                        <div>· 내 상품: <b>{categoryMismatch.myCategory}</b></div>
                                        <div>· TOP 10 주요: <b>{categoryMismatch.topCategory}</b></div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <Button danger size="small" onClick={() => { setCategoryMismatch(null); autoAnalyzeRef.current = false; setAiResults([]); }}>키워드 다시 입력</Button>
                                        <Button size="small" onClick={() => { autoAnalyzeRef.current = true; setCategoryMismatch(null); fetchTop10Tags(top10); }} style={{ color: '#f97316', borderColor: '#fed7aa' }}>그래도 계속 진행</Button>
                                    </div>
                                </div>
                            )}

                            {/* 분석 카드 */}
                            <Card
                                bordered={false}
                                style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                                title={
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <ThunderboltOutlined style={{ color: '#f59e0b', fontSize: 13 }} /> 상품명·태그·카테고리 최적화
                                    </span>
                                }
                                extra={
                                    <Button type="primary" size="small" icon={<ThunderboltOutlined />}
                                        onClick={handleAiAnalyze} loading={aiLoading}
                                        disabled={tagsLoading || !!categoryMismatch}
                                        style={{ background: aiResults.length > 0 ? '#6366f1' : undefined, fontSize: 12 }}>
                                        {aiResults.length > 0 ? '다시 분석' : '분석하기'}
                                    </Button>
                                }
                            >
                                {aiResults.length === 0 && !aiLoading && (
                                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>
                                        {categoryMismatch ? '위 카테고리 불일치 안내를 확인해주세요.'
                                            : tagsLoading ? '태그 수집 완료 후 자동으로 분석합니다...'
                                            : '분석하기 버튼을 눌러 최적안을 받아보세요.'}
                                    </div>
                                )}
                                {aiLoading && (
                                    <div style={{ textAlign: 'center', padding: '32px 0' }}>
                                        <Spin size="large" />
                                        <div style={{ marginTop: 10, color: '#6366f1', fontSize: 12 }}>태그 검색량 분석 중...</div>
                                    </div>
                                )}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                    {aiResults.map((ar, resultIdx) => {
                                        const { analysis, tagVolumes: vols, topCategory } = ar;
                                        const isLatest = resultIdx === 0;
                                        return (
                                            <div key={resultIdx}>
                                            <div ref={el => { cardRefs.current[resultIdx] = el; }} style={{ border: isLatest ? '2px solid #6366f1' : '1px solid #e2e8f0', borderRadius: 12, padding: '16px', background: isLatest ? '#fafafe' : '#f8fafc', position: 'relative' }}>
                                                <div data-no-capture="true" style={{ position: 'absolute', top: -11, left: 14, background: isLatest ? '#6366f1' : '#94a3b8', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 10 }}>
                                                    {isLatest ? '최신' : `${resultIdx + 1}번째`}
                                                </div>
                                                <div data-no-capture="true" style={{ position: 'absolute', top: -14, right: 8 }}>
                                                    <Button size="small" icon={<DownloadOutlined />} loading={savingIdx === resultIdx}
                                                        onClick={() => handleSaveImage(resultIdx)}
                                                        style={{ fontSize: 11, color: '#6366f1', borderColor: '#c7d2fe', background: '#f5f3ff' }}>
                                                        이미지 저장
                                                    </Button>
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                                                    {/* 추천 상품명 */}
                                                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', background: '#eef2ff', padding: '2px 8px', borderRadius: 4 }}>추천 상품명</span>
                                                                <span style={{ fontSize: 10, color: '#f97316', fontWeight: 600 }}>스토어명·모델명·옵션명은 필요 시 개별적으로 넣어주세요</span>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <span style={{ fontSize: 11, fontWeight: 600, color: (analysis.recommendedTitle?.length || 0) > 50 ? '#ef4444' : (analysis.recommendedTitle?.length || 0) >= 40 ? '#f59e0b' : '#10b981' }}>
                                                                    {analysis.recommendedTitle?.length || 0} / 50자
                                                                </span>
                                                                <CopyBtn text={analysis.recommendedTitle} />
                                                            </div>
                                                        </div>
                                                        <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', lineHeight: 1.5, marginBottom: 6 }}>{analysis.recommendedTitle}</div>
                                                        <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>{analysis.titleReason}</div>
                                                    </div>

                                                    {/* 추천 태그 */}
                                                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                                            <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', background: '#eef2ff', padding: '2px 8px', borderRadius: 4 }}>추천 태그 <span style={{ fontWeight: 400, color: '#94a3b8' }}>{analysis.recommendedTags?.length || 0}개</span></span>
                                                            <CopyBtn text={analysis.recommendedTags?.join(', ') || ''} />
                                                        </div>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                                                            {(analysis.recommendedTags || []).map(tag => {
                                                                const vol = vols?.[tag];
                                                                const isMy = myTags.has(tag);
                                                                return (
                                                                    <div key={tag} style={{ border: `1px solid ${isMy ? '#a5b4fc' : '#e2e8f0'}`, borderRadius: 6, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 5, background: isMy ? '#eef2ff' : '#fff' }}>
                                                                        {isMy && <span style={{ fontSize: 9, fontWeight: 700, color: '#6366f1', background: '#c7d2fe', borderRadius: 3, padding: '1px 4px', lineHeight: 1.4 }}>MY</span>}
                                                                        <span style={{ fontSize: 12, fontWeight: 600, color: isMy ? '#4338ca' : '#1e293b' }}>#{tag}</span>
                                                                        {vol != null && <span style={{ fontSize: 10, color: '#94a3b8' }}>{fmt(vol)}</span>}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>{analysis.tagsReason}</div>
                                                    </div>

                                                    {/* 예비 키워드 */}
                                                    {analysis.reserveTags?.length > 0 && (
                                                        <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', background: '#eef2ff', padding: '2px 8px', borderRadius: 4 }}>예비 키워드 <span style={{ fontWeight: 400, color: '#94a3b8' }}>{analysis.reserveTags.length}개</span></span>
                                                                    <span style={{ fontSize: 10, color: '#94a3b8' }}>추천 10개 교체 시 참고</span>
                                                                </div>
                                                                <CopyBtn text={analysis.reserveTags.join(', ')} />
                                                            </div>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                                                                {analysis.reserveTags.map(tag => {
                                                                    const vol = vols?.[tag];
                                                                    const isMy = myTags.has(tag);
                                                                    return (
                                                                        <div key={tag} style={{ border: `1px solid ${isMy ? '#a5b4fc' : '#e2e8f0'}`, borderRadius: 6, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 5, background: isMy ? '#eef2ff' : '#fff' }}>
                                                                            {isMy && <span style={{ fontSize: 9, fontWeight: 700, color: '#6366f1', background: '#c7d2fe', borderRadius: 3, padding: '1px 4px', lineHeight: 1.4 }}>MY</span>}
                                                                            <span style={{ fontSize: 12, fontWeight: 500, color: isMy ? '#4338ca' : '#64748b' }}>#{tag}</span>
                                                                            {vol != null && <span style={{ fontSize: 10, color: '#94a3b8' }}>{fmt(vol)}</span>}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            {analysis.reserveTagsReason && <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>{analysis.reserveTagsReason}</div>}
                                                        </div>
                                                    )}

                                                    {/* 카테고리 + 인사이트 */}
                                                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                                                            <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', width: 58, flexShrink: 0, paddingTop: 1 }}>카테고리</span>
                                                            <span style={{ fontSize: 11, color: '#334155', flex: 1, lineHeight: 1.6 }}>{analysis.categoryAdvice}</span>
                                                        </div>
                                                        {uniqueCategories.length > 0 && (
                                                            <div style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                                                                <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', width: 58, flexShrink: 0, paddingTop: 1 }}>TOP 10<br/>카테고리</span>
                                                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                    {uniqueCategories.map((cat, i) => (
                                                                        <span key={i} style={{ fontSize: 11, color: '#64748b', lineHeight: 1.6 }}>{cat}</span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 0' }}>
                                                            <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', width: 58, flexShrink: 0, paddingTop: 1 }}>인사이트</span>
                                                            <span style={{ fontSize: 11, color: '#334155', flex: 1, lineHeight: 1.7, whiteSpace: 'pre-line' }}>{analysis.keyInsight}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card>
                        </Col>

                    </Row>
                </>
            )}

            <Modal
                title="최근 조회 기록"
                open={showHistoryModal}
                onCancel={() => setShowHistoryModal(false)}
                footer={<Button onClick={() => setShowHistoryModal(false)}>닫기</Button>}
                width={680}
                styles={{ body: { maxHeight: 520, overflowY: 'auto', padding: '8px 0' } }}
            >
                <Spin spinning={historyLoading && historyData.length === 0}>
                    {historyData.length === 0 && !historyLoading && (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                            조회 기록이 없습니다.
                        </div>
                    )}
                    {historyData.map(item => {
                        const parts = Object.fromEntries(
                            item.input.split('|').map(p => {
                                const idx = p.indexOf('=');
                                return [p.slice(0, idx), p.slice(idx + 1)];
                            })
                        );
                        return (
                            <div
                                key={item.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    padding: '12px 16px',
                                    borderBottom: '1px solid #f1f5f9',
                                }}
                            >
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <div style={{ fontSize: 12, color: '#64748b' }}>
                                        <span style={{ fontWeight: 600, color: '#94a3b8', marginRight: 6 }}>키워드</span>
                                        <Typography.Text strong style={{ fontSize: 13, color: '#0f172a' }}>{parts.keyword || '-'}</Typography.Text>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#64748b' }}>
                                        <span style={{ fontWeight: 600, color: '#94a3b8', marginRight: 6 }}>URL</span>
                                        <Typography.Text style={{ fontSize: 12, wordBreak: 'break-all', color: '#334155' }}>{parts.url || '-'}</Typography.Text>
                                    </div>
                                    <Typography.Text type="secondary" style={{ fontSize: 11, marginTop: 2 }}>
                                        {item.updated_at || item.searched_at}
                                    </Typography.Text>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                                    <Button size="small" type="primary" onClick={() => handleApplyHistory(item.input)} style={{ borderRadius: 6 }}>선택</Button>
                                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteHistory(item.id)} style={{ borderRadius: 6 }} />
                                </div>
                            </div>
                        );
                    })}
                    {historyHasMore && (
                        <div style={{ textAlign: 'center', padding: '12px 0' }}>
                            <Button loading={historyLoading} onClick={() => fetchHistory(historyOffset, true)}>더보기</Button>
                        </div>
                    )}
                </Spin>
            </Modal>
            <KakaoFloatButton />
        </div>
        </div>
    );
};

export default ProductGenerate;
